import React from 'react';
import {
  Box,
  Container,
  Typography,
  Divider,
} from '@mui/material';

const TermsAndConditions = () => {
  return (
    <Box
      sx={{
        minHeight: '100vh',
        position: 'relative',
        background: 'linear-gradient(180deg, #0a0a0a 0%, #1a1a1a 50%, #0a0a0a 100%)',
      }}
    >
      {/* Background gradient overlay */}
      <Box
        sx={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'radial-gradient(circle at 50% 0%, rgba(25, 118, 210, 0.15) 0%, transparent 50%)',
          pointerEvents: 'none',
          zIndex: 0,
        }}
      />
      {/* Header Section with Dark Background */}
      <Box
        sx={{
          bgcolor: 'rgba(26, 26, 26, 0.9)',
          color: 'rgba(255, 255, 255, 0.95)',
          py: 6,
          mb: 4,
          position: 'relative',
          zIndex: 1,
          borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
        }}
      >
        <Container maxWidth="md">
          <Typography 
            variant="h3" 
            component="h1" 
            gutterBottom 
            sx={{ fontWeight: 'bold', textAlign: 'center' }}
          >
            Terms and Conditions
          </Typography>
          <Typography 
            variant="h6" 
            sx={{ textAlign: 'center', opacity: 0.9, mt: 2 }}
          >
            Legal agreement for use of our Speech-to-Text Service
          </Typography>
        </Container>
      </Box>

      <Container maxWidth="md" sx={{ mb: 6, position: 'relative', zIndex: 1 }}>
        {/* Introduction Section */}
        <Box sx={{ mb: 5 }}>
          <Typography variant="h5" component="h2" gutterBottom sx={{ fontWeight: 'bold', mb: 2 }}>
            1. Introduction
          </Typography>
          <Typography variant="body1" paragraph sx={{ lineHeight: 1.8 }}>
            These Terms and Conditions ("Terms") govern the use of the Speech-to-Text service ("Service") operated by sovavoice.com ("we", "us", "our").
          </Typography>
          <Typography variant="body1" paragraph sx={{ lineHeight: 1.8 }}>
            By accessing or using the Service, you agree to be bound by these Terms.
          </Typography>
        </Box>

        <Divider sx={{ my: 4 }} />

        {/* Definitions Section */}
        <Box sx={{ mb: 5 }}>
          <Typography variant="h5" component="h2" gutterBottom sx={{ fontWeight: 'bold', mb: 2 }}>
            2. Definitions
          </Typography>
          <Typography variant="body1" paragraph sx={{ lineHeight: 1.8 }}>
            <strong>Service</strong> – the file-based speech-to-text transcription service.
          </Typography>
          <Typography variant="body1" paragraph sx={{ lineHeight: 1.8 }}>
            <strong>User</strong> – any individual or entity using the Service.
          </Typography>
          <Typography variant="body1" paragraph sx={{ lineHeight: 1.8 }}>
            <strong>Content</strong> – audio files uploaded by the User and resulting transcription output.
          </Typography>
        </Box>

        <Divider sx={{ my: 4 }} />

        {/* Service Description Section */}
        <Box sx={{ mb: 5 }}>
          <Typography variant="h5" component="h2" gutterBottom sx={{ fontWeight: 'bold', mb: 2 }}>
            3. Service Description
          </Typography>
          <Typography variant="body1" paragraph sx={{ lineHeight: 1.8 }}>
            The Service allows Users to upload audio files which are processed and transcribed into text using automated speech recognition technology.
          </Typography>
          <Typography variant="body1" paragraph sx={{ lineHeight: 1.8 }}>
            The Service is file-based and does not provide real-time transcription or live dictation.
          </Typography>
        </Box>

        <Divider sx={{ my: 4 }} />

        {/* User Accounts Section */}
        <Box sx={{ mb: 5 }}>
          <Typography variant="h5" component="h2" gutterBottom sx={{ fontWeight: 'bold', mb: 2 }}>
            4. User Accounts
          </Typography>
          <Typography variant="body1" paragraph sx={{ lineHeight: 1.8 }}>
            Some features of the Service may require user registration. Users are responsible for maintaining the confidentiality of their account credentials.
          </Typography>
        </Box>

        <Divider sx={{ my: 4 }} />

        {/* User Content Section */}
        <Box sx={{ mb: 5 }}>
          <Typography variant="h5" component="h2" gutterBottom sx={{ fontWeight: 'bold', mb: 2 }}>
            5. User Content
          </Typography>
          <Typography variant="body1" paragraph sx={{ lineHeight: 1.8 }}>
            Users retain full ownership of their uploaded audio files and resulting transcriptions.
          </Typography>
          <Typography variant="body1" paragraph sx={{ lineHeight: 1.8 }}>
            By uploading Content, the User grants us a limited, non-exclusive, temporary license to process the Content solely for the purpose of providing the Service.
          </Typography>
        </Box>

        <Divider sx={{ my: 4 }} />

        {/* Data Processing & Storage Section */}
        <Box sx={{ mb: 5 }}>
          <Typography variant="h5" component="h2" gutterBottom sx={{ fontWeight: 'bold', mb: 2 }}>
            6. Data Processing & Storage
          </Typography>
          <Typography variant="body1" paragraph sx={{ lineHeight: 1.8 }}>
            Uploaded audio files and generated transcripts may be temporarily stored for processing and debugging purposes.
          </Typography>
          <Typography variant="body1" paragraph sx={{ lineHeight: 1.8 }}>
            We do not permanently store audio files unless explicitly stated. Temporary data is automatically deleted after a limited retention period.
          </Typography>
          <Typography variant="body1" paragraph sx={{ lineHeight: 1.8 }}>
            Details regarding personal data processing are described in our Privacy Policy.
          </Typography>
        </Box>

        <Divider sx={{ my: 4 }} />

        {/* Acceptable Use Section */}
        <Box sx={{ mb: 5 }}>
          <Typography variant="h5" component="h2" gutterBottom sx={{ fontWeight: 'bold', mb: 2 }}>
            7. Acceptable Use
          </Typography>
          <Typography variant="body1" paragraph sx={{ lineHeight: 1.8 }}>
            Users agree not to:
          </Typography>
          <Box component="ul" sx={{ pl: 4, mb: 2 }}>
            <li>
              <Typography variant="body1" sx={{ lineHeight: 1.8, mb: 1 }}>
                upload illegal, harmful, or infringing content,
              </Typography>
            </li>
            <li>
              <Typography variant="body1" sx={{ lineHeight: 1.8, mb: 1 }}>
                attempt to abuse, overload, or reverse engineer the Service,
              </Typography>
            </li>
            <li>
              <Typography variant="body1" sx={{ lineHeight: 1.8, mb: 1 }}>
                use the Service for unlawful surveillance or data collection.
              </Typography>
            </li>
          </Box>
          <Typography variant="body1" paragraph sx={{ lineHeight: 1.8 }}>
            We reserve the right to suspend or terminate access in case of misuse.
          </Typography>
        </Box>

        <Divider sx={{ my: 4 }} />

        {/* Payments & Billing Section */}
        <Box sx={{ mb: 5 }}>
          <Typography variant="h5" component="h2" gutterBottom sx={{ fontWeight: 'bold', mb: 2 }}>
            8. Payments & Billing
          </Typography>
          <Typography variant="body1" paragraph sx={{ lineHeight: 1.8 }}>
            Some features may require payment. Pricing, usage limits, and billing terms will be clearly communicated before any charges occur.
          </Typography>
          <Typography variant="body1" paragraph sx={{ lineHeight: 1.8 }}>
            All fees are based on audio processing time unless stated otherwise.
          </Typography>
        </Box>

        <Divider sx={{ my: 4 }} />

        {/* Availability & Changes Section */}
        <Box sx={{ mb: 5 }}>
          <Typography variant="h5" component="h2" gutterBottom sx={{ fontWeight: 'bold', mb: 2 }}>
            9. Availability & Changes
          </Typography>
          <Typography variant="body1" paragraph sx={{ lineHeight: 1.8 }}>
            The Service is provided on an "as is" and "as available" basis.
          </Typography>
          <Typography variant="body1" paragraph sx={{ lineHeight: 1.8 }}>
            We may modify, suspend, or discontinue parts of the Service at any time without prior notice.
          </Typography>
        </Box>

        <Divider sx={{ my: 4 }} />

        {/* Disclaimer of Warranties Section */}
        <Box sx={{ mb: 5 }}>
          <Typography variant="h5" component="h2" gutterBottom sx={{ fontWeight: 'bold', mb: 2 }}>
            10. Disclaimer of Warranties
          </Typography>
          <Typography variant="body1" paragraph sx={{ lineHeight: 1.8 }}>
            We do not guarantee that:
          </Typography>
          <Box component="ul" sx={{ pl: 4, mb: 2 }}>
            <li>
              <Typography variant="body1" sx={{ lineHeight: 1.8, mb: 1 }}>
                transcriptions will be error-free,
              </Typography>
            </li>
            <li>
              <Typography variant="body1" sx={{ lineHeight: 1.8, mb: 1 }}>
                the Service will be uninterrupted,
              </Typography>
            </li>
            <li>
              <Typography variant="body1" sx={{ lineHeight: 1.8, mb: 1 }}>
                output will meet specific professional or legal requirements.
              </Typography>
            </li>
          </Box>
          <Typography variant="body1" paragraph sx={{ lineHeight: 1.8 }}>
            The Service should not be used as a sole source for legal, medical, or compliance-critical decisions.
          </Typography>
        </Box>

        <Divider sx={{ my: 4 }} />

        {/* Limitation of Liability Section */}
        <Box sx={{ mb: 5 }}>
          <Typography variant="h5" component="h2" gutterBottom sx={{ fontWeight: 'bold', mb: 2 }}>
            11. Limitation of Liability
          </Typography>
          <Typography variant="body1" paragraph sx={{ lineHeight: 1.8 }}>
            To the maximum extent permitted by law, we shall not be liable for:
          </Typography>
          <Box component="ul" sx={{ pl: 4, mb: 2 }}>
            <li>
              <Typography variant="body1" sx={{ lineHeight: 1.8, mb: 1 }}>
                loss of data,
              </Typography>
            </li>
            <li>
              <Typography variant="body1" sx={{ lineHeight: 1.8, mb: 1 }}>
                loss of profits,
              </Typography>
            </li>
            <li>
              <Typography variant="body1" sx={{ lineHeight: 1.8, mb: 1 }}>
                indirect or consequential damages arising from the use of the Service.
              </Typography>
            </li>
          </Box>
          <Typography variant="body1" paragraph sx={{ lineHeight: 1.8 }}>
            Total liability shall not exceed the amount paid by the User for the Service in the preceding 12 months.
          </Typography>
        </Box>

        <Divider sx={{ my: 4 }} />

        {/* Termination Section */}
        <Box sx={{ mb: 5 }}>
          <Typography variant="h5" component="h2" gutterBottom sx={{ fontWeight: 'bold', mb: 2 }}>
            12. Termination
          </Typography>
          <Typography variant="body1" paragraph sx={{ lineHeight: 1.8 }}>
            We may terminate or suspend access to the Service at any time for violation of these Terms.
          </Typography>
          <Typography variant="body1" paragraph sx={{ lineHeight: 1.8 }}>
            Users may stop using the Service at any time.
          </Typography>
        </Box>

        <Divider sx={{ my: 4 }} />

        {/* Governing Law Section */}
        <Box sx={{ mb: 5 }}>
          <Typography variant="h5" component="h2" gutterBottom sx={{ fontWeight: 'bold', mb: 2 }}>
            13. Governing Law
          </Typography>
          <Typography variant="body1" paragraph sx={{ lineHeight: 1.8 }}>
            These Terms shall be governed by and construed in accordance with the laws of [Your Country].
          </Typography>
        </Box>

        <Divider sx={{ my: 4 }} />

        {/* Contact Information Section */}
        <Box sx={{ mb: 5 }}>
          <Typography variant="h5" component="h2" gutterBottom sx={{ fontWeight: 'bold', mb: 2 }}>
            14. Contact Information
          </Typography>
          <Typography variant="body1" paragraph sx={{ lineHeight: 1.8 }}>
            For any questions regarding these Terms, contact us at:
          </Typography>
          <Typography variant="body1" paragraph sx={{ lineHeight: 1.8 }}>
            Email: <a href="mailto:sovavoice@networkninjas.cz" style={{ color: 'inherit', textDecoration: 'underline' }}>sovavoice@networkninjas.cz</a>
          </Typography>
        </Box>
      </Container>
    </Box>
  );
};

export default TermsAndConditions;
